import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";

export default function BMRCalculator() {
  const { t } = useTranslation();
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [activityLevel, setActivityLevel] = useState("1.2");
  const [results, setResults] = useState<any>(null);

  const calculateBMR = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    const activity = parseFloat(activityLevel);
    
    if (!w || !h || !a || w <= 0 || h <= 0 || a <= 0) return;
    
    let bmr: number;
    if (gender === "male") {
      bmr = 10 * w + 6.25 * h - 5 * a + 5;
    } else {
      bmr = 10 * w + 6.25 * h - 5 * a - 161;
    }
    
    const tdee = bmr * activity;
    
    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      weightLoss: Math.round(tdee - 500),
      weightGain: Math.round(tdee + 500)
    });
  };

  return (
    <PageLayout calculatorName="BMR Calculator" calculatorPath="/calculator/bmr">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('bmr.title')}</CardTitle>
            <CardDescription>{t('bmr.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('bmr.gender')}</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('bmr.male')}</SelectItem>
                  <SelectItem value="female">{t('bmr.female')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('bmr.weight')}</Label>
              <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="70" />
            </div>
            <div>
              <Label>{t('bmr.height')}</Label>
              <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="175" />
            </div>
            <div>
              <Label>{t('bmr.age')}</Label>
              <Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="25" />
            </div>
            <div>
              <Label>{t('bmr.activityLevel')}</Label>
              <Select value={activityLevel} onValueChange={setActivityLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1.2">{t('bmr.sedentary')}</SelectItem>
                  <SelectItem value="1.375">{t('bmr.lightlyActive')}</SelectItem>
                  <SelectItem value="1.55">{t('bmr.moderatelyActive')}</SelectItem>
                  <SelectItem value="1.725">{t('bmr.veryActive')}</SelectItem>
                  <SelectItem value="1.9">{t('bmr.extraActive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={calculateBMR} className="w-full">{t('common.calculate')}</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>{t('common.yourResults')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground">{t('bmr.bmrLabel')}</div>
                  <div className="text-3xl font-bold">{results.bmr} cal/day</div>
                  <div className="text-xs text-muted-foreground">{t('bmr.bmrDesc')}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{t('bmr.tdeeLabel')}</div>
                  <div className="text-3xl font-bold">{results.tdee} cal/day</div>
                  <div className="text-xs text-muted-foreground">{t('bmr.tdeeDesc')}</div>
                </div>
                <div className="border-t pt-4 space-y-2">
                  <div>
                    <div className="text-sm font-medium">{t('bmr.weightLossLabel')}</div>
                    <div className="text-xl font-semibold">{results.weightLoss} cal/day</div>
                    <div className="text-xs text-muted-foreground">{t('bmr.weightLossDesc')}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium">{t('bmr.weightGainLabel')}</div>
                    <div className="text-xl font-semibold">{results.weightGain} cal/day</div>
                    <div className="text-xs text-muted-foreground">{t('bmr.weightGainDesc')}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="md:col-span-2">
              <CalculatorSuperCalcCTA config={calculatorCTAs["bmr"]} trigger={results.bmr} />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
